import { useAppDispatch, useAppSelector } from '@/app/redux/hooks';
import { requireSessionUserDetails } from '@/app/redux/slices/sessionSlice';
import {
  selectUpgradeState,
  setBillingInfo,
  setConfirmationToken,
  setPaymentMethod,
} from '@/app/redux/slices/upgradeSlice';
import { UNKNOWN_ERROR_MESSAGE } from '@/common/ui/UnknownErrorMessage';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import PlanDetails from '@/dashboard/Upgrade/PlanDetails';
import { ArrowRightIcon, InfoIcon } from '@phosphor-icons/react';
import { Alert } from '@shadcn/components/alert';
import { Button } from '@shadcn/components/button';
import { Label } from '@shadcn/components/label';
import { RadioGroup, RadioGroupItem } from '@shadcn/components/radio-group';
import {
  AddressElement,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

export default function PaymentDetails() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();

  const { email } = useAppSelector(requireSessionUserDetails);
  const {
    seatCount,
    selectedPlan,
    billingCycle,
    paymentMethod,
    currency,
    coupon,
  } = useAppSelector(selectUpgradeState);

  const [isLoading, setLoading] = useState(false);

  const [billingAddressFilled, setBillingAddressFilled] = useState(false);
  const [cardDetailsFilled, setCardDetailsFilled] = useState(false);

  const handleCardPayment = async () => {
    if (!stripe || !elements) {
      throw new Error(
        `Stripe or Elements object are missing: stripe: ${!!stripe}, elements: ${!!elements}`,
      );
    }

    // Trigger form validation and wallet collection
    const { error: submitError } = await elements.submit();
    if (submitError) {
      throw new Error(submitError.message);
    }

    // Create the ConfirmationToken using the details collected by the Payment Element
    const { error, confirmationToken } = await stripe.createConfirmationToken({
      elements,
      params: {
        payment_method_data: {
          billing_details: {
            email,
          },
        },
      },
    });
    if (error) {
      throw new Error(error.message);
    }

    dispatch(setConfirmationToken(confirmationToken.id));
  };

  const handleSubmit = async () => {
    setLoading(true);

    try {
      // Get and store confirmation token for card payment
      if (paymentMethod === 'card') {
        await handleCardPayment();
      }

      trackMixpanelEvent('Proceeded from Payment Info to Confirmation', {
        selectedPlan,
        seatCount,
        billingCycle,
        paymentMethod,
        currency,
      });
      navigate('/checkout/confirmation');
    } catch (error) {
      console.error(error);
      toast.error('Failed to set up your plan', {
        description:
          error instanceof Error ? error.message : UNKNOWN_ERROR_MESSAGE,
      });
    }
    setLoading(false);
  };

  return (
    <div className="flex flex-col lg:flex-row gap-4 h-full">
      <div className="flex flex-col gap-4 p-1 w-full">
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-2">
            <span className="font-medium">Billing Address</span>
            <div className="flex flex-col gap-1">
              <span className="text-[14px] text-[#555]">Email</span>
              <span className="flex p-2 bg-muted rounded-sm border border-border text-muted-foreground font-light text-[16px]">
                {email}
              </span>
            </div>
            <AddressElement
              options={{
                mode: 'billing',
                fields: {
                  phone: 'always',
                },
              }}
              onChange={(e) => {
                setBillingAddressFilled(e.complete);
                dispatch(
                  setBillingInfo({
                    address: e.value.address,
                    email: email ?? '',
                    name: e.value.name ?? '',
                    phone: e.value.phone ?? null,
                    card: null,
                  }),
                );
              }}
            />
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <span className="font-medium">Payment Method</span>
          {selectedPlan !== 'family' && (
            <RadioGroup
              className="flex gap-4 mb-2"
              value={paymentMethod}
              onValueChange={(value) =>
                dispatch(setPaymentMethod(value as 'card' | 'invoice'))
              }
            >
              <Label className="flex items-center gap-2">
                <RadioGroupItem value="card"></RadioGroupItem>Card
              </Label>
              <Label className="flex items-center gap-2">
                <RadioGroupItem value="invoice"></RadioGroupItem>Invoice
              </Label>
            </RadioGroup>
          )}

          {paymentMethod === 'card' && (
            <PaymentElement
              id="payment-element"
              onChange={(e) => {
                setCardDetailsFilled(e.complete);
              }}
            />
          )}
          {paymentMethod === 'invoice' && (
            <Alert className="text-sm">
              <InfoIcon size={18} className="ml-2" />
              <span>
                Your invoices will be sent to the email address provided above.
              </span>
            </Alert>
          )}
        </div>
      </div>

      <div className="flex flex-col w-auto min-w-[280px]">
        <PlanDetails
          plan={selectedPlan}
          seatCount={seatCount ?? 1}
          billingCycle={billingCycle}
          currency={currency}
          coupon={coupon}
          onEdit={() => {
            navigate('/pricing');
          }}
        />
        <Button
          className="w-full mt-4 gap-2"
          onClick={handleSubmit}
          loading={isLoading}
          disabled={
            !billingAddressFilled ||
            (paymentMethod === 'card' && !cardDetailsFilled)
          }
        >
          Continue to Confirmation
          <ArrowRightIcon size={16} />
        </Button>
      </div>
    </div>
  );
}
